/**
 * @file Builds the menu items for the investor account's title actions.
 */
import React from "react";
import type { MenuProps } from "antd";
import { UserAddOutlined, RedoOutlined, UserDeleteOutlined } from "@ant-design/icons";

import { hasAccess } from "@components/Authorization";
import { useAppContext } from "@components/AppContext";
import { type AppContext } from "@components/AppContext/types";
import { isExternalInvestorPortalEnabled } from "@components/Organization";

import useActions from "./useActions.js";

const LABEL_ENABLE = "Grant Access";
const LABEL_DISABLE = "Revoke Access";
const LABEL_CREATE_USER = "Create User";
const LABEL_RESET_PASSWORD = "Reset Password";
const TITLE_EXTERNAL_IDENTITY_PROVIDER =
  "An external identity provider manages the investors of this organization.";

type MenuItem = Required<MenuProps>["items"][number];

type Investor = {
  email?: string;
};

type Account = {
  id: string;
  investor: Investor;
  isDisabled?: boolean;
  correlationId?: string;
  lastAuthenticatedAt?: string;
};

type Actions = {
  enableAccount?: () => void;
  disableAccount?: () => void;
};

/**
 * Builds the create-user, reset-password and grant or revoke access items.
 */
const useItems = (
  account: Account | undefined,
  updateAccount: (updatedAccount: Account) => void,
  openResetIdentityModal: () => void,
  openCreateIdentityModal: () => void,
): MenuItem[] => {
  const { getOrganizationId } = useAppContext() as AppContext;

  const {
    enableAccount,
    disableAccount,
  } = useActions(account, updateAccount) as Actions;

  const organizationId = getOrganizationId();

  // NOTE: an external identity provider holds the investor identities of these
  //       organizations, therefore the portal cannot make a user or reset a
  //       password.
  const isExternalPortalEnabled = isExternalInvestorPortalEnabled(organizationId);

  const externalPortalTitle = isExternalPortalEnabled
    ? TITLE_EXTERNAL_IDENTITY_PROVIDER
    : undefined;

  const items: MenuItem[] = [];

  if (!account) {
    return items;
  }

  const {
    investor,
    isDisabled,
    correlationId,
    lastAuthenticatedAt,
  } = account;

  const hasEmail = !!investor.email;
  const hasIdentity = !!correlationId;
  const isNeverSignedIn = !lastAuthenticatedAt;

  const canWriteAccounts = hasAccess(["accounts-write"]);

  const isCreateIdentityAvailable =
    canWriteAccounts &&
    hasEmail &&
    !hasIdentity;

  const isResetIdentityAvailable =
    canWriteAccounts &&
    hasEmail &&
    hasIdentity &&
    isNeverSignedIn;

  if (isCreateIdentityAvailable) {
    items.push({
      key: "createIdentity",
      icon: <UserAddOutlined />,
      label: LABEL_CREATE_USER,
      title: externalPortalTitle,
      onClick: openCreateIdentityModal,
      disabled: isExternalPortalEnabled,
    });
  }

  if (isResetIdentityAvailable) {
    items.push({
      key: "resetIdentity",
      icon: <RedoOutlined />,
      label: LABEL_RESET_PASSWORD,
      title: externalPortalTitle,
      onClick: openResetIdentityModal,
      disabled: isExternalPortalEnabled,
    });
  }

  if (isDisabled) {
    items.push({
      key: "grantAccess",
      icon: <UserAddOutlined />,
      label: LABEL_ENABLE,
      onClick: enableAccount,
    });

  } else {
    items.push({
      key: "revokeAccess",
      icon: <UserDeleteOutlined />,
      label: LABEL_DISABLE,
      danger: true,
      onClick: disableAccount,
    });

  }

  return items;
};

export default useItems;
